<template>
  <div :class="`page-view ${subpage ? 'subpage' : ''}`" id="page">
    <div class="assumed-to" v-if="assumed || assumeParam">
      <i class="icon-hardhat"></i>
      <div class="assumed">
        <span>Assumed</span>
        <span>{{ account ? account.name : ''}}</span>
        <span :class="`account-id ${copiedID ? 'copied' : ''}`" @click="copyID(account.id)">(#{{ account ? account.id : '' }})</span>
      </div>
      <div class="unassume" @click="unassumeAccount()">
        Unassume
      </div>
    </div>
    <NotificationsTopBar />

    <header :class="`${!!$slots.tabs ? 'has-tabs' : ''}`">
      <nav class="breadcrumbs">
        <template v-if="backLink !== '' && backText !== ''">
          <a class="back" :href="backLink" @click.prevent="$router.push(backLink)" v-if="backLink && backText">
            <i class="mdi mdi-arrow-left" />
            {{ backText }}
          </a>
        </template>
        <slot name="id" />
      </nav>
      <div class="content">
        <div class="content-info">
          <h1 :class="`${longTitle ? 'shrink' : ''}`">{{ title | truncate(100) }}</h1>
          <slot name="below-title" />
        </div>
        <div class="d-f ai-c jc-c">
          <slot name="header-right" />
        </div>
      </div>
      <nav class="tabs" v-if="!!$slots.tabs">
        <slot name="tabs" />
      </nav>
    </header>
    <section :class="`main ${!!$slots.side ? 'has-sidebar' : ''}`">
      <section :class="`content ${!!$slots.tabs ? 'tabs' : ''} ${overflow ? 'overflow' : '' }`">
        <slot />
      </section>
      <aside v-if="!!$slots.side">
        <slot name="side" />
      </aside>
    </section>
    <footer v-if="!!$slots.bottom || !!$slots['bottom-side']">
      <slot name="bottom" />
      <slot name="bottom-side" />
    </footer>
    <transition mode="out-in" name="toggle-route">
      <footer v-if="!!$slots.floatingBottom" class="floating-bottom p-a-4">
        <slot name="floatingBottom" />
      </footer>
    </transition>
  </div>
</template>

<script>

import NotificationsTopBar from '@/components/Notifications/TopBar.vue';
import { truncate } from '@/utils';

export default {
  components: {
    NotificationsTopBar,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    noBackLink: {
      type: Boolean,
      default: false,
    },
    backLink: {
      type: String,
      default: '',
    },
    backText: {
      type: String,
      default: '',
    },
    subpage: {
      type: Boolean,
      default: false,
    },
    longTitle: {
      type: Boolean,
      default: false,
    },
    overflow: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    lastRoute() {
      return this.$store.getters['Theme/lastRoute'];
    },
    /**
     * Return boolean if the page has tabs
     * @returns {Boolean}
     */
    hasTabs() {
      return !!this.$slots.tabs;
    },
  },
  methods: {
    unassumeAccount() {
      this.$store.dispatch('AccountManager/unassumeAccount');
    },
    /**
     * Copy account-id
     * @returns {Void}
     */
    copyID(id) {
      clearTimeout(this.copyTimer);
      window.Clipboard.copy(id);
      this.copiedID = true;
      this.copyTimer = setTimeout(() => {
        this.copiedID = false;
      }, 500);
    },
  },
  data() {
    return {
      copiedID: false,
      copyTimer: null,
    };
  },
  filters: {
    /**
     * Truncate long strings
     * @param {String} string
     * @param {Number} length Defaults to 100
     * @returns {String}
     */
    truncate(string, length = 100) {
      return truncate.apply(string, [length, true]);
    },
  },
};
</script>

<style lang="scss">


.floating-bottom {
  background: #FFFFFF;
  box-shadow: 0 -2px 4px 0 rgba(165,179,194,0.20), 0 0 1px 0 rgba(165,179,194,0.50);
  bottom: 0;
  position: sticky;
}

.assumed-to + .notifications {
  @media (max-width: 1023px) {
    top: calc(var(--sidebar-height) + 40px);
  }
}

.assumed-to {
  align-items: center;
  background-color: white;
  display: flex;
  background: #FFFFFF;
  box-shadow: 0 2px 4px 0 rgba(165,179,194,0.20), 0 0 1px 0 rgba(165,179,194,0.50);
  padding: 8px 16px 8px 12px;
  height: 40px;
  position: sticky;
  top: var(--sidebar-height);
  z-index: 4;

  @media (min-width: 1024px) {
    display: none;
  }

  .account-id {
    color: #868B97;
    font-size: 12px;
    transition: color .3s;
    text-overflow: unset;
    overflow: unset;
    font-family: Menlo, Consolas, monospace;

    &.copied {
      color: #1643C9;
    }
  }

  i {
    color: #868B97;
    font-size: 22px;
    margin-right: 8px;
  }

  .assumed {
    align-items: center;
    padding-right: 12px;
    display: flex;
    min-width: 0;

    span {
      margin-right: 5px;

      &:nth-child(1) {
        color: #868B97;
      }

      &:nth-child(2) {
        color: #2F2F2F;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .unassume {
    color: #1643C9;
    margin-left: auto;
  }
}

.sidebar.collapsed ~ .page-view {
  @media (min-width: 1024px) {
    padding-left: var(--sidebar-width-collapsed);
  }
}

.page-view {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 56px;
  transition: padding-left .3s;

  &.max-height {
    height: 100vh;

    & > .main {
      .content {
        overflow: visible !important;
      }
      @media (min-width: 768px) {
        overflow: hidden;
      }
    }
  }

  &[data-full-content="true"] {
    section.main {
      padding: 0;
    }
  }

  .page-placeholder-text {
    h2 {
      font-size: 26px;
      color: #000000;
      text-align: center;
      line-height: 50px;
      font-weight: 500;
    }

    p {
      font-size: 14px;
      color: #848893;
      text-align: center;
      line-height: 20px;
    }
  }

  &.subpage {
    & > header {
      background-color: white;
    }
  }

  & > header {
    .breadcrumbs {
      align-items: center;
      display: flex;
      padding: 8px 16px;
      height: 48px;

      .back {
        align-items: center;
        display: flex;
        color: var(--color-cta-blue);
        font-size: 14px;
        font-weight: 500;
        line-height: 32px;

        i {
          font-size: 20px;
          margin-right: 5px;
        }
      }

      .id {
        margin-left: auto;
      }
    }

    & > .content {
      display: flex;
      padding: 0 16px 16px;
      width: 100%;

      .content-info {
        margin-right: auto;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      h1 {
        font-size: 28px;
        font-weight: 500;
        line-height: 34px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        padding-right: 20px;

        &.shrink {
          font-size: 20px;
          line-height: 24px;
        }
      }
    }

    .tabs {
      border-top: 1px solid #EEF0F9;
      display: flex;

      & > * {
        flex: 1;
      }
    }
  }

  & > .main {
    padding: 12px 6px;
    display: flex;
    flex: 1;

    & > .content {
      flex: 1;
      display: flex;
      flex-direction: column;
      // overflow-x: hidden;
      text-overflow: ellipsis;
      min-width: 0;

      &.overflow {
        // overflow-x: hidden;
      }
    }
  }

  @media (min-width: 640px) {
    & > .main {
      padding: 16px;
    }

    & > header {
      & > .content {
        padding-left: 24px;
      }

      .breadcrumbs {
        padding-left: 24px;
      }
    }
  }

  @media (min-width: 768px) {
    & > header {
      & > .content {
        h1 {
          font-size: 36px;;
          line-height: 42px;

          &.shrink {
            font-size: 28px;
            line-height: 32px;
          }
        }
      }

      .tabs {
        padding-left: 40px;

        & > * {
          flex: none;
        }
      }
    }
  }

  @media (min-width: 1024px) {
    padding-top: 0;
    padding-left: var(--sidebar-width);

    & > header {
      .breadcrumbs {
        padding-left: 40px;
        height: 58px;
      }

      & > .content {
        padding-left: 40px;

        h1 {
          font-size: 42px;
          line-height: 48px;

          &.shrink {
            font-size: 34px;
            line-height: 38px;
          }
        }
      }
    }

    & > .main {
      padding: 32px 32px;
    }
  }

  &.toggle-route-enter-active, &.toggle-route-leave-active {
    transition: opacity .4s, transform 0.4s;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    z-index: 1;
  }
  &.toggle-route-enter {
    opacity: 0;
  }

  &.toggle-route-leave-to {
    opacity: 0;
  }
}
</style>
